import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * useFocusTrap — shared keyboard behaviour for every dialog in the app.
 *
 * While `active`:
 *  - focus moves into the dialog (first focusable element, else the panel)
 *  - Tab / Shift+Tab cycle inside the dialog instead of escaping to the page
 *  - Escape calls onClose (when provided)
 *  - on close, focus returns to whatever had it before the dialog opened
 *
 * Usage:
 *   const panelRef = useRef(null);
 *   useFocusTrap(panelRef, isOpen, onClose);
 *   ...
 *   <div ref={panelRef} role="dialog" aria-modal="true" tabIndex={-1}>
 */
export function useFocusTrap(panelRef, active, onClose) {
  // onClose is nearly always an inline arrow from the parent, so its identity
  // changes on EVERY parent render. It must not sit in the dep array: a re-run
  // tears the trap down (restoring focus to whatever opened the dialog) and
  // then re-focuses the panel's first focusable. On a phone that shows and
  // hides the on-screen keyboard, and since the keyboard's own resize event
  // re-renders App, it feeds itself - the "keyboard flickers in and out while
  // typing in a dialog" bug. Read it through a ref instead.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!active) return undefined;
    const panel = panelRef.current;
    if (!panel) return undefined;

    const previouslyFocused = document.activeElement;

    const focusables = () => [...panel.querySelectorAll(FOCUSABLE)]
      .filter(el => el.offsetParent !== null || el === document.activeElement);

    // The dialog shell renders its close button before the content, so
    // "first focusable" is the X in every dialog - wrong whenever there is a
    // field to fill in. Content can name its own target with data-autofocus
    // (use that INSTEAD of React's autoFocus, so only one thing takes focus).
    const initial = panel.querySelector('[data-autofocus]') || focusables()[0] || panel;
    initial.focus({ preventScroll: true });

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onCloseRef.current) {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;

      if (e.shiftKey && (current === first || !panel.contains(current))) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && (current === last || !panel.contains(current))) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [panelRef, active]);
}

export default useFocusTrap;
